'use client'
import React, { useMemo, useCallback, useState, useEffect } from 'react'
import dynamic from 'next/dynamic'

import { useTranslations, useLocale } from 'next-intl'

import { languageOptions, currencyOptions } from '@/src/constants/header'
import {
    getSupportHref,
    getUserMenuItems,
    getFlyinNavLinks,
    getTranslationKey,
    getUserDisplayName
} from '@/src/utils/header/headerUtils'
import { useHeaderAuth } from '@/src/hooks/header/useHeaderAuth'
import { useHeaderCurrency } from '@/src/hooks/header/useHeaderCurrency'
import { useHeaderLanguage } from '@/src/hooks/header/useHeaderLanguage'
import { useHeaderVisibility } from '@/src/hooks/header/useHeaderVisibility'
import { useHeaderUI } from '@/src/hooks/header/useHeaderUI'

import { FlyinHeader } from './FlyinHeader'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ './SignInModal/SignInModalWrapper'),
    { ssr: false }
)

interface HeaderContainerProps {
    hideSignIn?: boolean
    showVisa?: boolean
    showFlightsHotels?: boolean
    /** Active vertical resolved server-side (from the request path) for nav highlighting. */
    activeVertical?: string | null
}

const HeaderContainer: React.FC<HeaderContainerProps> = ({
    hideSignIn = false,
    showVisa = false,
    showFlightsHotels = true,
    activeVertical = null
}) => {
    const locale = useLocale()
    const t = useTranslations('header')
    const tCurrency = useTranslations('Currency')

    const { user, handleSignOut, isMounted } = useHeaderAuth()
    const currencyHook = useHeaderCurrency(isMounted)
    const languageHook = useHeaderLanguage('flyin', isMounted)
    const visibility = useHeaderVisibility('flyin')
    const uiHook = useHeaderUI(user.signedIn)

    const [selected, setSelected] = useState({
        language: languageHook.selectedLanguage,
        currency: currencyOptions[0] // Always start with default to prevent hydration mismatch
    })

    useEffect(() => {
        setSelected(prev => ({
            ...prev,
            language: languageHook.selectedLanguage
        }))
    }, [languageHook.selectedLanguage])

    useEffect(() => {
        if (!isMounted) return // Don't update until client-side hydration is complete
        if (!currencyHook.isLoading && !currencyHook.isError && currencyHook.selectedCurrency) {
            const found = currencyHook.currencyOptions.find(opt => opt.code === currencyHook.selectedCurrency!.code)
            if (found) {
                setSelected(prev => ({
                    ...prev,
                    currency: found
                }))
            }
        } else if (!currencyHook.isLoading && !currencyHook.isError && !currencyHook.selectedCurrency) {
            // If no selected currency, use default
            setSelected(prev => ({
                ...prev,
                currency: currencyOptions[0]
            }))
        }
    }, [isMounted, currencyHook.selectedCurrency, currencyHook.isLoading, currencyHook.isError, currencyHook.currencyOptions])

    const navLinks = useMemo(
        () => getFlyinNavLinks(locale, activeVertical, showVisa, showFlightsHotels),
        [locale, activeVertical, showVisa, showFlightsHotels]
    )
    const userMenuItems = useMemo(() => getUserMenuItems(t, locale), [t, locale])
    const supportHref = useMemo(() => getSupportHref(locale), [locale])
    const userDisplayName = useMemo(() => getUserDisplayName(user), [user])

    const options = useMemo(
        () => ({
            language: languageOptions,
            currency: currencyHook.currencyOptions
        }),
        [currencyHook.currencyOptions]
    )

    const onSelect = useCallback(
        (type: 'language' | 'currency', value: any) => {
            setSelected(prev => ({ ...prev, [type]: value }))
            if (type === 'currency') {
                currencyHook.onCurrencySelect(value)
            } else if (type === 'language') {
                languageHook.onLanguageSelect(value)
            }
            uiHook.onMenuClose(type)
        },
        [currencyHook, languageHook, uiHook]
    )

    const onSignOut = useCallback(async () => {
        uiHook.onMenuClose('user')
        await handleSignOut()
    }, [handleSignOut, uiHook])

    const displayUser = isMounted ? user : { signedIn: false }

    return (
        <>
            <FlyinHeader
                navLinks={navLinks}
                options={options}
                selected={selected}
                menuAnchors={{
                    ...uiHook.menuAnchors,
                    user: uiHook.menuStates.user ? uiHook.menuAnchors.user : null
                }}
                onMenuOpen={uiHook.onMenuOpen}
                onMenuClose={uiHook.onMenuClose}
                onSelect={onSelect}
                modals={uiHook.modals}
                onModalOpen={uiHook.onModalOpen}
                onModalClose={uiHook.onModalClose}
                t={t}
                tCurrency={tCurrency}
                user={displayUser}
                userDisplayName={userDisplayName}
                userMenuItems={userMenuItems}
                onSignOut={onSignOut}
                supportHref={supportHref}
                locale={locale}
                showLangCurrDropdown={visibility.showLangCurrDropdown}
                showDeals={visibility.showDeals}
                isOffersPage={visibility.isOffersPage}
                hideSignIn={hideSignIn || visibility.shouldHideSignIn}
            />
            <SignInModalWrapper
                open={uiHook.modals.signIn}
                onClose={() => uiHook.onModalClose('signIn')}
                onMenuClose={() => uiHook.onMenuClose('user')}
            />
        </>
    )
}

export { HeaderContainer as Header }
export default HeaderContainer
